import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney } from '../utils/formatters';
import { toAbsoluteUrl } from '../lib/media';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import AppHeader from '../components/AppHeader';
import './OpportunityDetails.css';

const HeartIcon = ({ filled }) => (
  <svg width="20" height="20" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const MapPinIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const FlagIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

export default function OpportunityDetails() {
  const { oppId } = useParams();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [opp, setOpp] = useState(null);
  const [poster, setPoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [subjects, setSubjects] = useState([]);

  // Report modal
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // Apply with CV
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [coverNote, setCoverNote] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [hasCv, setHasCv] = useState(null);

  useEffect(() => {
    fetchOpportunity();
    fetchSubjects();
    if (user && token) {
      checkSaved();
      checkApplication();
    }
  }, [oppId, user, token]);

  const checkApplication = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/my-applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const existing = data.applications?.find(a => a.opp_id === oppId);
        if (existing) setHasApplied(true);
      }
      // Check if user has a CV
      const cvRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/autofill`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (cvRes.ok) setHasCv(true);
    } catch (e) { console.error(e); }
  };

  const submitApplication = async () => {
    if (applying) return;
    setApplying(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${oppId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cover_note: coverNote }),
      });
      if (res.ok) {
        setHasApplied(true);
        setShowApplyModal(false);
        setCoverNote('');
      } else {
        const err = await res.json();
        alert(err.detail || 'Error');
      }
    } catch (e) { console.error(e); }
    setApplying(false);
  };

  const fetchOpportunity = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${oppId}`);
      if (res.ok) {
        const data = await res.json();
        setOpp(data.opportunity);
        setPoster(data.poster);
      } else {
        navigate('/student-hub');
      }
    } catch (e) {
      console.error(e);
      navigate('/student-hub');
    }
    setLoading(false);
  };

  const fetchSubjects = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/subjects`);
      if (res.ok) setSubjects(await res.json());
    } catch (e) { console.error(e); }
  };

  const checkSaved = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items?item_type=opportunity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsSaved(data.saved_items?.some(s => s.item_id === oppId) || false);
      }
    } catch (e) { console.error(e); }
  };

  const toggleSave = async () => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }
    
    try {
      if (isSaved) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items/opportunity/${oppId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setIsSaved(false);
      } else {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ item_type: 'opportunity', item_id: oppId })
        });
        setIsSaved(true);
      }
    } catch (e) { console.error(e); }
  };

  const submitReport = async () => {
    if (!reportReason) return;
    
    setSubmittingReport(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_type: 'opportunity',
          item_id: oppId,
          reason: reportReason,
          details: reportDetails
        })
      });
      setShowReport(false);
      setReportReason('');
      setReportDetails('');
    } catch (e) { console.error(e); }
    setSubmittingReport(false);
  };

  const getSubjectName = (key) => {
    const subject = subjects.find(s => s.key === key);
    if (!subject) return key;
    return lang === 'ar' ? subject.name_ar : subject.name_en;
  };

  const isExpired = opp?.deadline && new Date(opp.deadline) < new Date();

  if (loading) {
    return (
      <div className="opp-details-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="opp-details-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!opp) {
    return null;
  }

  return (
    <div className="opp-details-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="opp-details">
      <AppHeader
        title={t('opps.detailsTitle')}
        backFallback="/student-hub"
        testId="opp-details-header"
        rightSlot={
          <button className={`save-header-btn ${isSaved ? 'saved' : ''}`} onClick={toggleSave}>
            <HeartIcon filled={isSaved} />
          </button>
        }
      />

      {/* Content */}
      <div className="opp-details-content">
        {/* Main Info */}
        <div className="opp-main-card">
          <div className="opp-badges-row">
            <Badge className={`type-badge type-${opp.type}`}>
              {t(`opps.type.${opp.type}`)}
            </Badge>
            <Badge variant="outline">
              {t(`opps.orgType.${opp.org_type}`)}
            </Badge>
            {opp.is_paid && (
              <Badge className="paid-badge">{t('opps.paid.yes')}</Badge>
            )}
            {isExpired && (
              <Badge variant="destructive">{t('opps.expired')}</Badge>
            )}
          </div>

          <h2 className="opp-title">{opp.title}</h2>
          <p className="opp-org">{opp.org_name}</p>

          <div className="opp-meta-row">
            <div className="meta-item">
              <MapPinIcon />
              <span>{t(`opps.locationType.${opp.location_type || opp.locationType || 'onsite'}`)}</span>
              {opp.location && <span>• {opp.location}</span>}
            </div>
            {opp.duration && (
              <div className="meta-item">
                <ClockIcon />
                <span>{opp.duration}</span>
              </div>
            )}
            {opp.is_paid && opp.stipend && (
              <div className="meta-item">
                <DollarIcon />
                <span>{formatMoney(opp.stipend, opp.currency_code || 'SDG', lang)}</span>
              </div>
            )}
          </div>

          {opp.deadline && (
            <div className={`deadline-banner ${isExpired ? 'expired' : ''}`}>
              <CalendarIcon />
              <span>
                {t('opps.deadlineLabel')}: {new Date(opp.deadline).toLocaleDateString(lang === 'ar' ? 'ar-SD' : 'en-US')}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {opp.description && (
          <div className="opp-section">
            <h3>{t('opps.fields.description')}</h3>
            <p className="description-text">{opp.description}</p>
          </div>
        )}

        {/* Requirements */}
        <div className="opp-section">
          <h3>{t('opps.fields.requirements')}</h3>
          
          {opp.education_level && (
            <div className="requirement-item">
              <span className="req-label">{t('opps.fields.educationLevel')}:</span>
              <span className="req-value">{t(`profile.educationLevels.${opp.education_level}`)}</span>
            </div>
          )}

          {opp.majors && opp.majors.length > 0 && (
            <div className="requirement-item">
              <span className="req-label">{t('opps.fields.majors')}:</span>
              <div className="tags-row">
                {opp.majors.map((m, i) => (
                  <Badge key={i} variant="outline">{getSubjectName(m)}</Badge>
                ))}
              </div>
            </div>
          )}

          {opp.required_skills && opp.required_skills.length > 0 && (
            <div className="requirement-item">
              <span className="req-label">{t('opps.fields.skills')}:</span>
              <div className="tags-row">
                {opp.required_skills.map((s, i) => (
                  <Badge key={i} variant="secondary">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {opp.interests && opp.interests.length > 0 && (
            <div className="requirement-item">
              <span className="req-label">{t('opps.fields.interests')}:</span>
              <div className="tags-row">
                {opp.interests.map((int, i) => (
                  <Badge key={i} variant="outline">{int}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* How to Apply */}
        {opp.apply_link && (
          <div className="opp-section">
            <h3>{t('opps.fields.apply')}</h3>
            <a href={opp.apply_link} target="_blank" rel="noopener noreferrer" className="apply-link">
              <ExternalLinkIcon />
              {t('opps.applyExternal')}
            </a>
          </div>
        )}

        {/* Poster Info */}
        {poster && (
          <div className="poster-card">
            <div className="poster-avatar">
              {poster.avatar ? (
                <img src={toAbsoluteUrl(poster.avatar)} alt={poster.name} />
              ) : (
                <span>{poster.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <div className="poster-info">
              <span className="poster-label">{t('opps.poster')}</span>
              <span className="poster-name">{poster.name}</span>
            </div>
          </div>
        )}

        {/* Posted Date */}
        {opp.created_at && (
          <p className="posted-date">
            {t('opps.postedOn')}: {new Date(opp.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SD' : 'en-US')}
          </p>
        )}

        {/* Report Button */}
        {user && (
          <button className="report-btn" onClick={() => setShowReport(true)}>
            <FlagIcon />
            {t('opps.actions.report')}
          </button>
        )}
      </div>

      {/* CTA */}
      <div className="opp-details-cta">
        {hasApplied ? (
          <Button className="cta-btn cta-applied" disabled data-testid="applied-btn">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
            {isRTL ? 'تم التقديم' : 'Applied'}
          </Button>
        ) : user && token ? (
          <Button 
            className="cta-btn cta-apply"
            onClick={() => setShowApplyModal(true)}
            disabled={isExpired}
            data-testid="apply-cv-btn"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            {isRTL ? 'تقديم بالسيرة الذاتية' : 'Apply with CV'}
          </Button>
        ) : opp.apply_method === 'external' && opp.apply_link ? (
          <Button 
            className="cta-btn cta-apply"
            onClick={() => window.open(opp.apply_link, '_blank')}
            disabled={isExpired}
          >
            <ExternalLinkIcon />
            {t('opps.applyExternal')}
          </Button>
        ) : (
          <Button 
            className="cta-btn cta-apply"
            onClick={() => navigate('/welcome')}
          >
            {isRTL ? 'سجل الدخول للتقديم' : 'Login to Apply'}
          </Button>
        )}
        <Button 
          variant="outline"
          className={`cta-btn cta-save ${isSaved ? 'saved' : ''}`}
          onClick={toggleSave}
        >
          <HeartIcon filled={isSaved} />
          {isSaved ? t('opps.actions.unsave') : t('opps.actions.save')}
        </Button>
      </div>

      {/* Report Modal */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{t('report.title')}</DialogTitle>
          </DialogHeader>
          <div className="report-form">
            <div className="form-group">
              <label>{t('report.reason')}</label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger>
                  <SelectValue placeholder={t('report.reason')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spam">{t('report.reasons.spam')}</SelectItem>
                  <SelectItem value="inappropriate">{t('report.reasons.inappropriate')}</SelectItem>
                  <SelectItem value="scam">{t('report.reasons.scam')}</SelectItem>
                  <SelectItem value="duplicate">{t('report.reasons.duplicate')}</SelectItem>
                  <SelectItem value="other">{t('report.reasons.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <label>{t('report.details')}</label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder={t('report.details')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReport(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={submitReport} disabled={!reportReason || submittingReport}>
              {submittingReport ? t('report.submitting') : t('report.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply with CV Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تقديم بالسيرة الذاتية' : 'Apply with your CV'}</DialogTitle>
          </DialogHeader>
          <div className="apply-modal-body">
            {hasCv === false ? (
              <div className="no-cv-notice">
                <p>{isRTL ? 'لم يتم العثور على سيرة ذاتية. أنشئ سيرتك الذاتية أولاً.' : 'No CV found. Please create your CV first.'}</p>
                <Button onClick={() => navigate('/student-hub/cv-builder')}>
                  {isRTL ? 'انشاء السيرة الذاتية' : 'Create CV'}
                </Button>
              </div>
            ) : (
              <>
                <p className="apply-modal-info">
                  {isRTL ? 'سيتم إرسال سيرتك الذاتية المحفوظة مع التقديم.' : 'Your saved CV will be submitted with this application.'}
                </p>
                <div className="form-group">
                  <label>{isRTL ? 'ملاحظة (اختياري)' : 'Cover note (optional)'}</label>
                  <Textarea
                    value={coverNote}
                    onChange={e => setCoverNote(e.target.value)}
                    placeholder={isRTL ? 'اكتب ملاحظة قصيرة...' : 'Write a short note to the employer...'}
                    rows={3}
                    data-testid="cover-note-input"
                  />
                </div>
              </>
            )}
          </div>
          {hasCv !== false && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={submitApplication} disabled={applying} data-testid="submit-application-btn">
                {applying ? (isRTL ? 'جاري التقديم...' : 'Submitting...') : (isRTL ? 'تأكيد التقديم' : 'Submit Application')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
